/**
 * Specification Template Management Hook
 * 
 * Manages specification templates that can be reused across multiple jobs.
 * Specifications define technical requirements, standards, and compliance criteria
 * for procurement projects.
 * 
 * Features:
 * - CRUD operations for specification templates
 * - Filtering by category, type, and organization
 * - Full-text search across names and descriptions
 * - Template cloning for quick customization
 * - Usage tracking for popularity metrics
 * - Soft deletion via deactivation
 * 
 * Template Types:
 * - Standard: System-wide templates (admin-created)
 * - My Organization: Organization-specific templates
 * - Personal: User-specific templates
 * 
 * @module useSpecifications
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { SpecificationTemplate, SpecificationType, JobCategory } from '@/lib/types/job';

/**
 * Options for filtering specification templates
 */
interface UseSpecificationsOptions {
  /** Filter by job category */
  category?: JobCategory;
  /** Filter by specification type (standard, organization, personal) */
  specificationType?: SpecificationType;
  /** Full-text search across name and description */
  search?: string;
  /** Filter by organization (for organization-specific templates) */
  organizationId?: string;
}

/**
 * Hook for managing specification templates
 * 
 * Provides comprehensive specification template management with filtering,
 * searching, and CRUD operations. Templates are fetched with filters applied
 * and sorted by usage count (most popular first).
 * 
 * @param options - Optional filters for category, type, search, and organization
 * @returns {Object} Specifications state and management functions
 * 
 * @example
 * ```tsx
 * function SpecificationLibrary() {
 *   const {
 *     specifications,
 *     loading,
 *     createSpecification,
 *     cloneSpecification,
 *     incrementUsageCount
 *   } = useSpecifications({
 *     category: 'construction',
 *     specificationType: 'standard'
 *   });
 *   
 *   const handleUseTemplate = async (specId) => {
 *     await incrementUsageCount(specId);
 *     // Apply template to job
 *   };
 *   
 *   return (
 *     <div>
 *       {specifications.map(spec => (
 *         <SpecCard
 *           key={spec.id}
 *           spec={spec}
 *           onUse={() => handleUseTemplate(spec.id)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useSpecifications = (options: UseSpecificationsOptions = {}) => {
  const [specifications, setSpecifications] = useState<SpecificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSpecifications = async () => {
    try {
      setLoading(true);
      
      let query = (supabase as any)
        .from('specifications')
        .select('*')
        .eq('is_active', true)
        .order('usage_count', { ascending: false });

      if (options.organizationId) {
        query = query.eq('organization_id', options.organizationId);
      }

      if (options.category) {
        query = query.eq('category', options.category);
      }

      if (options.specificationType) {
        query = query.eq('specification_type', options.specificationType);
      }

      if (options.search) {
        query = query.textSearch('specification_name,specification_description', options.search);
      }

      const { data, error } = await query;

      if (error) throw error;

      setSpecifications((data || []) as SpecificationTemplate[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching specifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch specifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecifications();
  }, [options.category, options.specificationType, options.search, options.organizationId]);

  const createSpecification = async (specData: Partial<SpecificationTemplate & { is_standard?: boolean; specification_document_id?: string }>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id, role')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Only admins can create standard specifications
      const isStandard = specData.is_standard && profile.role === 'admin';

      const { data, error } = await (supabase as any)
        .from('specifications')
        .insert({
          ...specData,
          is_standard: isStandard,
          organization_id: profile.organization_id,
          created_by_user_id: user.id,
          last_updated_by_user_id: user.id,
        } as any)
        .select()
        .single();

      if (error) throw error;

      setSpecifications([data as SpecificationTemplate, ...specifications]);
      
      toast({
        title: 'Specification created',
        description: `Specification "${(data as any).specification_name}" has been created${isStandard ? ' as a standard template' : ''}`,
      });

      return data;
    } catch (err) {
      console.error('Error creating specification:', err);
      toast({
        title: 'Error creating specification',
        description: err instanceof Error ? err.message : 'Failed to create specification',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const updateSpecification = async (id: string, updates: Partial<SpecificationTemplate & { is_standard?: boolean; specification_document_id?: string }>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      // Only admins can modify is_standard flag
      const updateData: any = {
        ...updates,
        last_updated_by_user_id: user.id,
      };

      if (profile?.role !== 'admin') {
        delete updateData.is_standard;
      }

      // For document-based specs, only allow updating name and content (subcategory)
      const spec = specifications.find(s => s.id === id);
      if (spec && (spec as any).specification_document_id) {
        // Only allow these fields to be updated for document-based specs
        const allowedFields = ['specification_name', 'content', 'specification_description', 'last_updated_by_user_id'];
        Object.keys(updateData).forEach(key => {
          if (!allowedFields.includes(key)) {
            delete updateData[key];
          }
        });
      }

      const { data, error } = await (supabase as any)
        .from('specifications')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setSpecifications(specifications.map(t => t.id === id ? data as SpecificationTemplate : t));
      
      toast({
        title: 'Specification updated',
        description: `Specification "${(data as any).specification_name}" has been updated`,
      });

      return data;
    } catch (err) {
      console.error('Error updating specification:', err);
      toast({
        title: 'Error updating specification',
        description: err instanceof Error ? err.message : 'Failed to update specification',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const cloneSpecification = async (specId: string, newName: string) => {
    try {
      const spec = specifications.find(t => t.id === specId);
      if (!spec) throw new Error('Specification not found');

      // If document-based, we need to duplicate the document
      let newDocumentId: string | undefined;
      if ((spec as any).specification_document_id) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // Get the original document
        const { data: originalDoc } = await supabase
          .from('documents')
          .select('*')
          .eq('id', (spec as any).specification_document_id)
          .single();

        if (originalDoc) {
          // Download the file
          const { data: fileData } = await supabase.storage
            .from('documents')
            .download(originalDoc.storage_key);

          if (fileData) {
            // Generate new storage key
            const fileExt = originalDoc.original_filename.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
            const newEntityId = crypto.randomUUID();
            const storageKey = `${user.id}/specification/${newEntityId}/${fileName}`;

            // Upload to new location
            const { error: uploadError } = await supabase.storage
              .from('documents')
              .upload(storageKey, fileData);

            if (!uploadError) {
              // Create new document record
              const { data: newDoc, error: docError } = await supabase
                .from('documents')
                .insert({
                  organization_id: originalDoc.organization_id,
                  entity_type: 'specification',
                  entity_id: newEntityId,
                  filename: fileName,
                  original_filename: originalDoc.original_filename,
                  size_bytes: originalDoc.size_bytes,
                  content_type: originalDoc.content_type,
                  storage_key: storageKey,
                  uploaded_by_user_id: user.id,
                })
                .select()
                .single();

              if (!docError && newDoc) {
                newDocumentId = newDoc.id;
              }
            }
          }
        }
      }

      const clonedSpec = await createSpecification({
        specification_name: newName,
        specification_description: `Cloned from: ${spec.specification_name}`,
        category: spec.category,
        specification_type: spec.specification_type,
        content: spec.content,
        sections: spec.sections,
        required_fields: spec.required_fields,
        tags: [...(spec.tags || []), 'cloned'],
        specification_document_id: newDocumentId,
      });

      return clonedSpec;
    } catch (err) {
      console.error('Error cloning specification:', err);
      throw err;
    }
  };

  const incrementUsageCount = async (specId: string) => {
    try {
      const { data } = await (supabase as any)
        .from('specifications')
        .select('usage_count')
        .eq('id', specId)
        .single();
      
      if (data) {
        await (supabase as any)
          .from('specifications')
          .update({ usage_count: (data.usage_count || 0) + 1 })
          .eq('id', specId);
      }
    } catch (err) {
      console.error('Error incrementing usage count:', err);
    }
  };

  const deactivateSpecification = async (id: string) => {
    try {
      await (supabase as any)
        .from('specifications')
        .update({ is_active: false })
        .eq('id', id);

      setSpecifications(specifications.filter(t => t.id !== id));
      
      toast({
        title: 'Specification deactivated',
        description: 'Specification has been deactivated and removed from the active list',
      });
    } catch (err) {
      console.error('Error deactivating specification:', err);
      toast({
        title: 'Error deactivating specification',
        description: err instanceof Error ? err.message : 'Failed to deactivate specification',
        variant: 'destructive',
      });
      throw err;
    }
  };

  return {
    specifications,
    loading,
    error,
    refetch: fetchSpecifications,
    createSpecification,
    updateSpecification,
    cloneSpecification,
    incrementUsageCount,
    deactivateSpecification,
  };
};